/**
 * useConnectionStatus - Track WebSocket connection status for all channels
 */
import { useState, useEffect } from 'react'
import { getWebSocketManager, ConnectionStatus } from '../lib/websocket'

export type OverallStatus = 'connected' | 'connecting' | 'partial' | 'disconnected'

interface ConnectionInfo {
  channel: string
  status: ConnectionStatus
}

interface UseConnectionStatusResult {
  overall: OverallStatus
  channels: ConnectionInfo[]
  prices: ConnectionStatus
  alerts: ConnectionStatus
  market: ConnectionStatus
}

export function useConnectionStatus(): UseConnectionStatusResult {
  const [statuses, setStatuses] = useState<Record<string, ConnectionStatus>>({})

  useEffect(() => {
    const wsManager = getWebSocketManager()

    // Get initial statuses
    setStatuses(wsManager.getAllStatuses())

    // Listen for status changes
    const unsubscribe = wsManager.onStatusChange((channel, status) => {
      setStatuses(prev => ({
        ...prev,
        [channel]: status
      }))
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // Calculate overall status - market channel is the primary indicator
  const channels: ConnectionInfo[] = Object.entries(statuses).map(([channel, status]) => ({
    channel,
    status
  }))

  const marketStatus = statuses['market'] || 'disconnected'
  const pricesStatus = statuses['prices'] || 'disconnected'
  const anyConnecting = channels.some(c => c.status === 'connecting')

  // Show "Live" if market OR prices channel is connected (primary feeds)
  // Show "Connecting" if any channel is still connecting
  // Show "Offline" only if no primary channels are connected
  let overall: OverallStatus = 'disconnected'
  if (marketStatus === 'connected' || pricesStatus === 'connected') {
    overall = 'connected'
  } else if (anyConnecting) {
    overall = 'connecting'
  } else if (channels.length > 0 && channels.some(c => c.status === 'connected')) {
    overall = 'partial'
  }

  return {
    overall,
    channels,
    prices: pricesStatus,
    alerts: statuses['alerts'] || 'disconnected',
    market: marketStatus
  }
}

export default useConnectionStatus
